/**
 * A workflow as a picture (D401), the plain-data half: its steps in waves left to right (the plan graph's layout,
 * planGraphLayout.ts, since a workflow runs forward only), each step in the state its newest try reports when there
 * is a run, and each branch taken, not taken or still open. The words are the contract's (STATE_WORDS, OUTCOME_WORDS):
 * workflow, step, branch, approval, not needed. Never "node" or "edge" on screen.
 */
import { edgeOutcome, isFinal, OUTCOME_WORDS, STATE_WORDS, STEP_KIND_WORDS, stepClass, type ActionClass, type StepKind, type WorkflowDefinition, type WorkflowEdge, type WorkflowRun, type WorkflowState, type WorkflowStepRun } from '@aioe/shared';

import type { ArtTone } from '../ui/Art.tsx';

export const STEP_KIND_SHORT: Record<StepKind, string> = {
  agent: 'Specialist or team member',
  foundry: 'Foundry agent',
  code: 'Proven code',
  approval: 'A person approves',
  wait: 'Wait',
  workflow: 'Run a workflow',
  checkout: 'Get the code',
  run: 'Run a command',
  checks: 'GitHub checks',
  comment: 'Comment',
  review: 'Review the pull request',
  merge: 'Merge',
  deploy: 'Deploy',
};
export const KIND_TONE: Record<StepKind, ArtTone> = { agent: 'violet', foundry: 'violet', code: 'blue', approval: 'amber', wait: 'slate', workflow: 'violet', checkout: 'teal', run: 'teal', checks: 'teal', comment: 'teal', review: 'violet', merge: 'rose', deploy: 'rose' };
export const STATE_TONE: Record<WorkflowState, ArtTone> = { queued: 'slate', running: 'blue', waiting: 'amber', succeeded: 'green', failed: 'rose', cancelled: 'slate', skipped: 'slate' };
/** The status dot a state shows beside its word (ui.tsx StatusMark). */
export const STATE_MARK: Record<WorkflowState, 'idle' | 'connected' | 'medium' | 'online' | 'error' | 'offline' | 'disabled'> = { queued: 'idle', running: 'online', waiting: 'medium', succeeded: 'connected', failed: 'error', cancelled: 'offline', skipped: 'disabled' };

export type BranchState = 'taken' | 'not-taken' | 'open';

export interface PictureStep {
  id: string;
  name: string;
  kind: StepKind;
  actionClass: ActionClass;
  /** Who works an agent step, or what a code step does, in a few words. */
  detail: string;
  /** In a run: the newest try's state (a step with no try yet waits, or is not needed once the run ended). */
  state?: WorkflowState;
  /** In a run: what it ended as, in words (Approved, Done, Failed…). */
  outcome?: string;
  attempts?: number;
}

export interface PictureBranch {
  id: string;
  from: string;
  to: string;
  label?: string;
  state: BranchState;
}

function codeWords(step: Extract<WorkflowDefinition['steps'][number], { kind: 'code' }>): string {
  const r = step.run;
  if (r.kind === 'template') return 'Puts earlier answers together';
  if (r.kind === 'script') return `Runs ${r.command}`;
  switch (r.action) {
    case 'http.request':
      return `${r.method} ${hostOf(r.url)}${r.secret ? ` with ${r.secret}` : ''}`;
    case 'file.write':
      return `Writes ${r.path}`;
    case 'task.create':
      return 'Files a task';
    case 'task.comment':
      return 'Comments on a task';
    case 'pull-request.open':
      return 'Opens a pull request';
    case 'inbox.notify':
      return `Tells ${r.to === 'owner' ? 'the owner' : r.to === 'starter' ? 'whoever started it' : 'the project'}`;
    case 'report.publish':
      return `Publishes a report to ${r.to === 'library' ? 'the library' : r.to === 'team' ? 'the team' : 'the organisation'}`;
  }
}

function hostOf(url: string): string {
  try {
    return new URL(url.replace(/\{\{[^}]*\}\}/g, 'x')).hostname;
  } catch {
    return 'a web address';
  }
}

function agentWords(step: Extract<WorkflowDefinition['steps'][number], { kind: 'agent' }>, wf: WorkflowDefinition): string {
  const m = step.member;
  if ('param' in m) return wf.parameters?.find((p) => p.name === m.param)?.label ?? 'Asked when it is added';
  return m.kind === 'member' ? `${m.name} of the team` : (m.name ?? 'A specialist');
}

function approvalWords(step: Extract<WorkflowDefinition['steps'][number], { kind: 'approval' }>): string {
  const d = step.decide;
  const who = d.who === 'owners' ? "The project's owners decide" : d.who === 'members' ? "The project's members decide" : `${d.people.map((p) => p.name).join(', ')} decide`;
  return `${who}${d.notStarter ? ', never the starter' : ''}`;
}

const branchLabel = (from: WorkflowDefinition['steps'][number] | undefined, e: WorkflowEdge): string | undefined => {
  const outcome = edgeOutcome(e);
  const test = e.when?.test ? `${e.when.test.path} ${e.when.test.op.replace('-', ' ')}${e.when.test.value !== undefined ? ` ${String(e.when.test.value)}` : ''}` : e.when?.otherwise ? 'otherwise' : undefined;
  if (from?.kind === 'approval' && outcome) return OUTCOME_WORDS[outcome];
  if (outcome === 'failed') return test ? `Failed, ${test}` : 'If it fails';
  return test;
};

/** The steps and branches of a definition, in a run's states when one is given. */
export function workflowPicture(wf: WorkflowDefinition, run?: { run: WorkflowRun; steps: readonly WorkflowStepRun[] }): { steps: PictureStep[]; branches: PictureBranch[] } {
  const newest = new Map<string, WorkflowStepRun>();
  const tries = new Map<string, number>();
  for (const s of run?.steps ?? []) {
    // One element of a step that runs per element: the step's own record speaks for it.
    if (s.item) continue;
    tries.set(s.stepId, Math.max(tries.get(s.stepId) ?? 0, s.attempt));
    const held = newest.get(s.stepId);
    if (!held || s.attempt > held.attempt) newest.set(s.stepId, s);
  }
  const ended = run ? isFinal(run.run.state) : false;
  const steps: PictureStep[] = wf.steps.map((s) => {
    const rec = newest.get(s.id);
    const state: WorkflowState | undefined = run ? (rec?.state ?? (ended ? 'skipped' : 'queued')) : undefined;
    return {
      id: s.id,
      name: s.name,
      kind: s.kind,
      actionClass: stepClass(s),
      detail: s.kind === 'agent' ? agentWords(s, wf) : s.kind === 'code' ? codeWords(s) : s.kind === 'approval' ? approvalWords(s) : STEP_KIND_WORDS[s.kind],
      ...(state ? { state } : {}),
      ...(rec?.outcome && rec.state !== 'skipped' ? { outcome: OUTCOME_WORDS[rec.outcome] } : {}),
      ...(tries.get(s.id) ? { attempts: tries.get(s.id)! } : {}),
    };
  });
  const byId = new Map(wf.steps.map((s) => [s.id, s]));
  const branches: PictureBranch[] = wf.edges.map((e, i) => {
    const rec = newest.get(e.from);
    const want = edgeOutcome(e) ?? 'succeeded';
    let state: BranchState = 'open';
    if (rec?.state === 'skipped') state = 'not-taken';
    else if (rec && isFinal(rec.state) && rec.outcome) state = rec.outcome === want && !e.when?.test && !e.when?.otherwise ? 'taken' : rec.outcome === want && newest.get(e.to) && newest.get(e.to)!.state !== 'skipped' ? 'taken' : rec.outcome === want ? 'open' : 'not-taken';
    else if (rec && isFinal(rec.state)) state = 'not-taken';
    const label = branchLabel(byId.get(e.from), e);
    return { id: `b${i}:${e.from}->${e.to}`, from: e.from, to: e.to, ...(label ? { label } : {}), state };
  });
  return { steps, branches };
}

/** The state a step shows, in words. */
export const stateWords = (s: PictureStep): string => (s.state === 'succeeded' && s.outcome && s.kind === 'approval' ? s.outcome : s.state ? STATE_WORDS[s.state] : STEP_KIND_SHORT[s.kind]);
